import html2pdf from 'html2pdf.js';
import moment from 'moment';
import AiSaveToKnowledgeButton from '@/components/knowledge/AiSaveToKnowledgeButton.vue';
import { readAssistantChatStream } from '@/components/ai/read-assistant-chat-stream';
import { zzlStreamLog } from '@/components/ai/zzl-stream-debug';
import { buildZzlChatMessages } from '@/components/ai/zzl/zzl-chat-prompt';
import { buildZzlPdfFileStem, buildZzlSaveFileStem } from '@/components/ai/zzl/zzl-report-filename';

/** 左侧数据区 + 右侧生成报告，切换页面后需恢复 */
const ZZL_STREAM_CACHE_FIELDS = ['aiText', 'result', 'images', 'files'];

/**
 * 流式生成报告 + 复制 Markdown + 导出 PDF（依赖 $refs.reportPanel、userText、aiText、result、ai_executings）
 * 流式请求走 AiBot 注入的 this.$ai.chat（AIAssistant，stream:true 返回 reader）。
 */
export default {
  components: { AiSaveToKnowledgeButton },
  created() {
    if (Array.isArray(this.pageStateFields)) {
      ZZL_STREAM_CACHE_FIELDS.forEach((field) => {
        if (!this.pageStateFields.includes(field)) {
          this.pageStateFields.push(field);
        }
      });
      this._zzlStreamCacheViaPageState = true;
    }
  },
  mounted() {
    if (!this._zzlStreamCacheViaPageState) {
      this.__zzlRestoreStreamCache();
      window.addEventListener('beforeunload', this.__zzlSaveStreamCache);
    }
  },
  beforeDestroy() {
    if (!this._zzlStreamCacheViaPageState) {
      window.removeEventListener('beforeunload', this.__zzlSaveStreamCache);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!this._zzlStreamCacheViaPageState) {
      this.__zzlSaveStreamCache();
    }
    next();
  },
  computed: {
    zzlSaveFileName() {
      if (typeof this.zzlReportSaveStem === 'function') {
        const stem = this.zzlReportSaveStem();
        return Array.isArray(stem) ? buildZzlSaveFileStem(stem) : buildZzlSaveFileStem(stem);
      }
      if (typeof this.zzlReportPdfStem === 'function') {
        return buildZzlSaveFileStem(this.zzlReportPdfStem());
      }
      return buildZzlSaveFileStem('AI报告');
    },
  },
  methods: {
    async CreateByAI() {
      // 无条件入口日志，便于确认是否走到最新 bundle
      console.warn('[zzl-stream] CreateByAI entry', this.$route && this.$route.path);
      zzlStreamLog('CreateByAI called', {
        route: this.$route && this.$route.path,
        aiTextLen: (this.aiText || '').length,
        hasChat: typeof this.$ai.chat === 'function',
      });
      if (!this.aiText || this.aiText.length < 10) {
        this.Error('请先获取数据');
        return;
      }
      if (this.aiText.length > 500000) {
        this.Error('原始数据过长（暂建议 500000 字以内），请缩小范围或拆分处理');
        return;
      }
      this.ai_executings = true;
      this.result = '';
      const panel = this.$refs.reportPanel;
      if (panel && panel.beginStream) {
        panel.beginStream();
      }
      await this.$nextTick();
      try {
        if (typeof this.$ai.chat !== 'function') {
          throw new Error('AI 助手未就绪，请确认已加载 AiBot');
        }
        const payload = {
          model_id: 'deepseek-v4',
          messages: buildZzlChatMessages(this.userText, this.aiText),
          stream: true,
          value_type: 'text',
          thinking: true,
        };
        zzlStreamLog('chat payload', {
          model_id: payload.model_id,
          messageCount: payload.messages.length,
          promptLen: payload.messages.map((m) => m.content.length).join('+'),
        });
        this._zzlChatReader = null;
        const finalText = await readAssistantChatStream(
          () => this.$ai.chat(payload).then((reader) => {
            this._zzlChatReader = reader;
            zzlStreamLog('$ai.chat returned reader', reader);
            return reader;
          }),
          (state) => {
            const p = this.$refs.reportPanel;
            const contentLen = (state && state.content) ? state.content.length : 0;
            if (contentLen <= 200 || contentLen % 500 < 50) {
              zzlStreamLog('setStreamState', {
                contentLen,
                reasoningLen: state && state.reasoning ? state.reasoning.length : 0,
                hasPanel: !!p,
              });
            }
            if (p && p.setStreamState) {
              p.setStreamState(state);
            }
          },
        );
        this.result = finalText || '';
      } catch (e) {
        console.error(e);
        this.Error(e && e.message ? e.message : e);
      } finally {
        this._zzlChatReader = null;
        this.ai_executings = false;
        const p = this.$refs.reportPanel;
        if (p && p.endStream) {
          p.endStream(this.result || '');
        }
        this.__zzlPersistStreamCache();
      }
    },
    __zzlStreamCacheKey() {
      const sid = (this.$store.getters['session/session'] || {}).id || 'anon';
      return `zzlStream_${sid}_${this.$route.path}`;
    },
    __zzlSaveStreamCache() {
      try {
        const data = ZZL_STREAM_CACHE_FIELDS.reduce((acc, field) => {
          acc[field] = this[field];
          return acc;
        }, {});
        localStorage.setItem(this.__zzlStreamCacheKey(), JSON.stringify(data));
      } catch (e) {
        console.warn('zzl stream cache save failed', e);
      }
    },
    __zzlRestoreStreamCache() {
      try {
        const raw = localStorage.getItem(this.__zzlStreamCacheKey());
        if (!raw) return;
        const data = JSON.parse(raw);
        ZZL_STREAM_CACHE_FIELDS.forEach((field) => {
          if (data[field] !== undefined) {
            this[field] = data[field];
          }
        });
      } catch (e) {
        console.warn('zzl stream cache restore failed', e);
      }
    },
    __zzlPersistStreamCache() {
      if (this._zzlStreamCacheViaPageState && typeof this.__savePageState === 'function') {
        this.__savePageState();
      } else {
        this.__zzlSaveStreamCache();
      }
    },
    async copyReportMd() {
      if (!this.result) {
        return;
      }
      await this._zzlCopyText(this.result, '已复制报告 Markdown');
    },
    /** 复制左侧清洗数据中的图片 URL 列表（名称 + 链接），依赖 this.images */
    async copyImageAddresses() {
      const imgs = this.images || [];
      if (!imgs.length) {
        this.Error('暂无图片地址');
        return;
      }
      const lines = imgs.map((item, i) => {
        const url = typeof item === 'string'
          ? item
          : (item && (item.url || item.href)) || '';
        const name = (item && item.name) ? String(item.name) : `图片${i + 1}`;
        return `${name}\t${url}`;
      });
      await this._zzlCopyText(lines.join('\n'), '已复制图片名称与地址');
    },
    exportPDF() {
      if (!this.result) {
        return;
      }
      const stem = typeof this.zzlReportPdfStem === 'function'
        ? this.zzlReportPdfStem()
        : buildZzlPdfFileStem(['AI报告']);
      const opt = {
        margin: 1,
        filename: `${stem}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      };
      const el = this.$refs.reportPanel && this.$refs.reportPanel.getPdfElement
        ? this.$refs.reportPanel.getPdfElement()
        : null;
      if (el) {
        html2pdf().set(opt).from(el).save();
      }
    },
    async _zzlCopyText(text, okMsg) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          document.body.appendChild(textarea);
          textarea.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(textarea);
          if (!ok) {
            throw new Error('copy failed');
          }
        }
        this.Success(okMsg || '复制成功');
      } catch (err) {
        console.error(err);
        this.Error('复制失败');
      }
    },
  },
};
